/**
 * audienceEngine.js
 *
 * Pure in-memory filter engine for the Audience Builder.
 * Translates declarative filter rules into JavaScript predicates run against
 * already-loaded context data (leads, tasks, quotes, invoices).
 *
 * Backward-compatible API: quote/invoice data is passed as an optional 5th arg.
 * Old callers that pass (leads, tasks, filters, logic) continue to work unchanged.
 *
 * Data sources:
 *   leads    – companies/{cId}/leads      — from LeadsContext
 *   tasks    – companies/{cId}/tasks      — from TasksContext  (type='job')
 *   quotes   – companies/{cId}/quotes     — from AudienceContext real-time listener
 *   invoices – companies/{cId}/invoices   — from AudienceContext real-time listener
 *
 * Filter categories: Lead · Job · Quote · Invoice
 */

// ── Filter Field Registry ─────────────────────────────────────────────────────

export const FILTER_FIELDS = [
  // ── Lead ──────────────────────────────────────────────────────────────────
  {
    key: 'leadStage',
    label: 'Lead Stage',
    category: 'Lead',
    operators: ['equals', 'not_equals', 'any_of'],
    valueType: 'leadStage',
  },
  {
    key: 'serviceType',
    label: 'Service Type',
    category: 'Lead',
    operators: ['equals', 'not_equals', 'any_of'],
    valueType: 'serviceType',
  },
  {
    key: 'lastContactDate',
    label: 'Last Contact',
    category: 'Lead',
    operators: ['older_than_days', 'newer_than_days'],
    valueType: 'days',
  },
  {
    key: 'tags',
    label: 'Tags',
    category: 'Lead',
    operators: ['contains', 'not_contains'],
    valueType: 'text',
  },

  // ── Job ───────────────────────────────────────────────────────────────────
  {
    key: 'jobStatus',
    label: 'Job Status',
    category: 'Job',
    operators: ['equals', 'not_equals', 'any_of', 'has_none'],
    valueType: 'jobStatus',
  },

  // ── Quote ─────────────────────────────────────────────────────────────────
  {
    key: 'quoteStatus',
    label: 'Quote Status',
    category: 'Quote',
    operators: ['equals', 'not_equals', 'any_of', 'has_none'],
    valueType: 'quoteStatus',
  },
  {
    key: 'quoteAge',
    label: 'Quote Age',
    category: 'Quote',
    operators: ['older_than_days', 'newer_than_days'],
    valueType: 'days',
  },
  {
    key: 'quoteApproved',
    label: 'Has Approved Quote',
    category: 'Quote',
    operators: ['is_true', 'is_false'],
    valueType: 'boolean',
  },
  {
    key: 'quoteRejected',
    label: 'Has Rejected Quote',
    category: 'Quote',
    operators: ['is_true', 'is_false'],
    valueType: 'boolean',
  },

  // ── Invoice ───────────────────────────────────────────────────────────────
  {
    key: 'invoiceStatus',
    label: 'Invoice Status',
    category: 'Invoice',
    operators: ['equals', 'not_equals', 'any_of', 'has_none'],
    valueType: 'invoiceStatus',
  },
  {
    key: 'invoicePaid',
    label: 'Invoice Paid',
    category: 'Invoice',
    operators: ['is_true', 'is_false'],
    valueType: 'boolean',
  },
  {
    key: 'invoiceOverdue',
    label: 'Invoice Overdue',
    category: 'Invoice',
    operators: ['is_true', 'is_false'],
    valueType: 'boolean',
  },
  {
    key: 'invoiceAmount',
    label: 'Invoice Amount ($)',
    category: 'Invoice',
    operators: ['greater_than', 'less_than'],
    valueType: 'currency',
  },
  {
    key: 'invoiceAge',
    label: 'Invoice Age',
    category: 'Invoice',
    operators: ['older_than_days', 'newer_than_days'],
    valueType: 'days',
  },
];

/** Map for O(1) field definition lookup */
export const FIELD_MAP = Object.fromEntries(FILTER_FIELDS.map((f) => [f.key, f]));

/** Unique, ordered categories for grouping the field selector */
export const FILTER_CATEGORIES = [...new Set(FILTER_FIELDS.map((f) => f.category))];

// ── Value Option Lists ────────────────────────────────────────────────────────

export const JOB_STATUS_OPTIONS = [
  { value: 'unscheduled', label: 'Unscheduled' },
  { value: 'scheduled',   label: 'Scheduled'   },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed'   },
  { value: 'cancelled',   label: 'Cancelled'   },
];

export const QUOTE_STATUS_OPTIONS = [
  { value: 'draft',    label: 'Draft'    },
  { value: 'sent',     label: 'Sent'     },
  { value: 'pending',  label: 'Pending'  },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired',  label: 'Expired'  },
];

export const INVOICE_STATUS_OPTIONS = [
  { value: 'sent',    label: 'Sent / Awaiting Payment' },
  { value: 'paid',    label: 'Paid'                    },
  { value: 'overdue', label: 'Overdue'                 },
];

export const OPERATOR_LABELS = {
  equals:           'equals',
  not_equals:       'does not equal',
  any_of:           'is any of',
  has_none:         'has none',
  older_than_days:  'older than',
  newer_than_days:  'within last',
  contains:         'contains',
  not_contains:     'does not contain',
  is_true:          'is true',
  is_false:         'is false',
  greater_than:     'greater than',
  less_than:        'less than',
};

// ── Rule Completeness ─────────────────────────────────────────────────────────

const NO_VALUE_OPERATORS = new Set(['has_none', 'is_true', 'is_false']);

/**
 * A rule is "active" (ready to filter) when it has a field, an operator,
 * and a valid value (unless the operator requires no value).
 */
export function isActiveRule(rule) {
  if (!rule || !rule.field || !rule.operator) return false;
  if (NO_VALUE_OPERATORS.has(rule.operator)) return true;
  if (Array.isArray(rule.value)) return rule.value.length > 0;
  return rule.value !== '' && rule.value !== null && rule.value !== undefined;
}

// ── Date Helper ───────────────────────────────────────────────────────────────

function toDate(raw) {
  if (!raw) return null;
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function ageDays(raw) {
  const d = toDate(raw);
  if (!d) return null;
  return (Date.now() - d.getTime()) / 86_400_000;
}

// ── Single-Rule Evaluator ─────────────────────────────────────────────────────

function evalRule(lead, rule, jobStatusByLeadId, quotesByLeadId, invoicesByLeadId) {
  const { field, operator, value } = rule;

  switch (field) {

    // ── Lead fields ──────────────────────────────────────────────────────────
    case 'leadStage': {
      const s = (lead.stage || '').trim();
      if (operator === 'equals')     return s === value;
      if (operator === 'not_equals') return s !== value;
      if (operator === 'any_of')     return Array.isArray(value) && value.includes(s);
      return true;
    }
    case 'serviceType': {
      const s = (lead.service || lead.serviceType || '').trim();
      if (operator === 'equals')     return s === value;
      if (operator === 'not_equals') return s !== value;
      if (operator === 'any_of')     return Array.isArray(value) && value.includes(s);
      return true;
    }
    case 'lastContactDate': {
      const age = ageDays(lead.updatedAt || lead.createdAt);
      if (age === null) return false;
      if (operator === 'older_than_days') return age > Number(value);
      if (operator === 'newer_than_days') return age <= Number(value);
      return true;
    }
    case 'tags': {
      const tags = (lead.tags || []).map((t) => String(t).toLowerCase());
      const needle = String(value || '').toLowerCase().trim();
      if (!needle) return true;
      const hit = tags.some((t) => t.includes(needle));
      if (operator === 'contains')     return hit;
      if (operator === 'not_contains') return !hit;
      return true;
    }

    // ── Job fields ───────────────────────────────────────────────────────────
    case 'jobStatus': {
      const statuses = jobStatusByLeadId[lead.id] || [];
      if (operator === 'has_none')   return statuses.length === 0;
      if (operator === 'equals')     return statuses.includes(value);
      if (operator === 'not_equals') return !statuses.includes(value);
      if (operator === 'any_of')     return Array.isArray(value) && value.some((v) => statuses.includes(v));
      return true;
    }

    // ── Quote fields ─────────────────────────────────────────────────────────
    case 'quoteStatus': {
      const qs = quotesByLeadId[lead.id] || [];
      if (operator === 'has_none')   return qs.length === 0;
      if (operator === 'equals')     return qs.some((q) => q.status === value);
      if (operator === 'not_equals') return qs.every((q) => q.status !== value);
      if (operator === 'any_of')     return qs.some((q) => Array.isArray(value) && value.includes(q.status));
      return true;
    }
    case 'quoteAge': {
      const qs = quotesByLeadId[lead.id] || [];
      if (qs.length === 0) return false;
      if (operator === 'older_than_days') {
        return qs.some((q) => {
          const age = ageDays(q.createdAt);
          return age !== null && age > Number(value);
        });
      }
      if (operator === 'newer_than_days') {
        return qs.some((q) => {
          const age = ageDays(q.createdAt);
          return age !== null && age <= Number(value);
        });
      }
      return true;
    }
    case 'quoteApproved': {
      const approved = (quotesByLeadId[lead.id] || []).some((q) => q.status === 'approved');
      if (operator === 'is_true')  return approved;
      if (operator === 'is_false') return !approved;
      return true;
    }
    case 'quoteRejected': {
      const rejected = (quotesByLeadId[lead.id] || []).some((q) => q.status === 'rejected');
      if (operator === 'is_true')  return rejected;
      if (operator === 'is_false') return !rejected;
      return true;
    }

    // ── Invoice fields ───────────────────────────────────────────────────────
    case 'invoiceStatus': {
      const inv = invoicesByLeadId[lead.id] || [];
      if (operator === 'has_none')   return inv.length === 0;
      if (operator === 'equals')     return inv.some((i) => i.status === value);
      if (operator === 'not_equals') return inv.every((i) => i.status !== value);
      if (operator === 'any_of')     return inv.some((i) => Array.isArray(value) && value.includes(i.status));
      return true;
    }
    case 'invoicePaid': {
      const paid = (invoicesByLeadId[lead.id] || []).some(
        (i) => i.status === 'paid' || !!i.paidAt
      );
      if (operator === 'is_true')  return paid;
      if (operator === 'is_false') return !paid;
      return true;
    }
    case 'invoiceOverdue': {
      // Overdue = invoice is unpaid AND created more than 30 days ago
      const overdue = (invoicesByLeadId[lead.id] || []).some((i) => {
        if (i.status === 'paid' || i.paidAt) return false;
        const age = ageDays(i.createdAt);
        return age !== null && age > 30;
      });
      if (operator === 'is_true')  return overdue;
      if (operator === 'is_false') return !overdue;
      return true;
    }
    case 'invoiceAmount': {
      const inv = invoicesByLeadId[lead.id] || [];
      const total = inv.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      if (operator === 'greater_than') return total > Number(value);
      if (operator === 'less_than')    return total < Number(value);
      return true;
    }
    case 'invoiceAge': {
      const inv = invoicesByLeadId[lead.id] || [];
      if (inv.length === 0) return false;
      if (operator === 'older_than_days') {
        return inv.some((i) => {
          const age = ageDays(i.createdAt);
          return age !== null && age > Number(value);
        });
      }
      if (operator === 'newer_than_days') {
        return inv.some((i) => {
          const age = ageDays(i.createdAt);
          return age !== null && age <= Number(value);
        });
      }
      return true;
    }

    default:
      return true;
  }
}

// ── Main Engine ───────────────────────────────────────────────────────────────

/**
 * Apply audience filter rules to a list of leads.
 *
 * @param {Object[]} leads        — All lead documents from LeadsContext
 * @param {Object[]} tasks        — All task documents from TasksContext
 * @param {Object[]} filters      — Array of { id, field, operator, value }
 * @param {'AND'|'OR'} filterLogic — 'AND' = every rule must match; 'OR' = any rule must match
 * @param {{ quotes?: Object[], invoices?: Object[] }} dataSources — optional extra data sources
 * @returns {Object[]} Filtered leads
 */
export function applyAudienceFilters(
  leads,
  tasks,
  filters,
  filterLogic = 'AND',
  { quotes = [], invoices = [] } = {}
) {
  const active = (filters || []).filter(isActiveRule);
  if (active.length === 0) return leads;

  // ── Pre-compute lookup maps ───────────────────────────────────────────────

  // leadId → effective job status array
  const jobStatusByLeadId = {};
  (tasks || [])
    .filter((t) => t.type === 'job')
    .forEach((t) => {
      if (!t.leadId) return;
      const effective = t.scheduledStart ? (t.status || 'scheduled') : 'unscheduled';
      if (!jobStatusByLeadId[t.leadId]) jobStatusByLeadId[t.leadId] = [];
      jobStatusByLeadId[t.leadId].push(effective);
    });

  // leadId → quote documents array
  const quotesByLeadId = {};
  (quotes || []).forEach((q) => {
    if (!q.leadId) return;
    if (!quotesByLeadId[q.leadId]) quotesByLeadId[q.leadId] = [];
    quotesByLeadId[q.leadId].push(q);
  });

  // leadId → invoice documents array
  const invoicesByLeadId = {};
  (invoices || []).forEach((i) => {
    if (!i.leadId) return;
    if (!invoicesByLeadId[i.leadId]) invoicesByLeadId[i.leadId] = [];
    invoicesByLeadId[i.leadId].push(i);
  });

  // ── Apply filters ─────────────────────────────────────────────────────────

  return (leads || []).filter((lead) => {
    const match = (r) => evalRule(lead, r, jobStatusByLeadId, quotesByLeadId, invoicesByLeadId);
    return filterLogic === 'OR'
      ? active.some(match)
      : active.every(match);
  });
}

/**
 * Build a compact human-readable summary of active filter rules.
 * Used in AudienceCard for a quick description.
 *
 * @param {Object[]} filters
 * @param {'AND'|'OR'} filterLogic
 * @returns {string}
 */
export function buildFilterSummary(filters, filterLogic = 'AND') {
  if (!filters || filters.length === 0) return 'All contacts';
  const active = filters.filter(isActiveRule);
  if (active.length === 0) return 'All contacts';

  const joiner = filterLogic === 'OR' ? '  OR  ' : '  AND  ';

  const parts = active.map((r) => {
    const fieldLabel = FIELD_MAP[r.field]?.label || r.field;
    const opLabel    = OPERATOR_LABELS[r.operator] || r.operator;
    const val        = NO_VALUE_OPERATORS.has(r.operator)
      ? ''
      : Array.isArray(r.value)
        ? r.value.join(', ')
        : (r.value ?? '');
    return val ? `${fieldLabel} ${opLabel} "${val}"` : `${fieldLabel} ${opLabel}`;
  });

  return parts.join(joiner);
}
